import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AppShell } from "../components/AppShell";
import { useAuth } from "../hooks/useAuth";
import {
  fetchAdminCoupons,
  createAdminCoupon,
  toggleAdminCoupon,
  deleteAdminCoupon,
  fetchAdminInstitutionById,
  fetchAdminOrganisationById,
} from "../api/admin";
import { InstitutionDetailsModal } from "../components/admin/InstitutionDetailsModal";
import { OrganisationDetailsModal } from "../components/admin/OrganisationDetailsModal";
import { C, font } from "../styles/tokens";

export function AdminCouponsPage() {
  const { localUser } = useAuth();
  const navigate = useNavigate();
  const isAdmin = localUser?.type === "PLATFORM_ADMIN";

  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  // Entity Details Modals
  const [selectedInstDetail, setSelectedInstDetail] = useState(null);
  const [selectedOrgDetail, setSelectedOrgDetail] = useState(null);
  const [loadingEntityId, setLoadingEntityId] = useState(null);

  // Filter states
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [typeFilter, setTypeFilter] = useState("ALL");

  // Create form state
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [code, setCode] = useState("");
  const [discountPercent, setDiscountPercent] = useState(100);
  const [accountTypeTarget, setAccountTypeTarget] = useState("BOTH"); // 'BOTH' | 'ORGANISATION' | 'ACADEMIC_INSTITUTION'
  const [targetRegistrationNumber, setTargetRegistrationNumber] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [maxUses, setMaxUses] = useState(1);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);

  // Action states
  const [togglingId, setTogglingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [copiedCode, setCopiedCode] = useState(null);

  useEffect(() => {
    if (!isAdmin) {
      navigate("/admin");
      return;
    }
    loadCoupons();
  }, [isAdmin]);

  async function loadCoupons() {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchAdminCoupons();
      const list = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res)
          ? res
          : [];
      setCoupons(list);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to load coupons.",
      );
    } finally {
      setLoading(false);
    }
  }

  async function handleOpenEntity(c) {
    if (!c) return;
    if (c.institution) {
      setLoadingEntityId(c.institution.id);
      setSelectedInstDetail(c.institution);
      try {
        const res = await fetchAdminInstitutionById(c.institution.id);
        const full = res?.data || res;
        if (full) {
          setSelectedInstDetail(full);
        }
      } catch (err) {
        console.error("Failed to load full institution details:", err);
      } finally {
        setLoadingEntityId(null);
      }
    } else if (c.organisation) {
      setLoadingEntityId(c.organisation.id);
      setSelectedOrgDetail(c.organisation);
      try {
        const res = await fetchAdminOrganisationById(c.organisation.id);
        const full = res?.data || res;
        if (full) {
          setSelectedOrgDetail(full);
        }
      } catch (err) {
        console.error("Failed to load full organisation details:", err);
      } finally {
        setLoadingEntityId(null);
      }
    }
  }

  function suggestCode(target = accountTypeTarget, pct = discountPercent) {
    const prefix =
      target === "ORGANISATION"
        ? "TAPORG"
        : target === "ACADEMIC_INSTITUTION"
          ? "TAPINST"
          : target === "PROFESSIONAL"
            ? "TAPVERIF"
            : "WELCOME";
    const rand = Math.random().toString(36).substring(2, 6).toUpperCase();
    return `${prefix}${pct}-${rand}`;
  }

  function handleOpenCreate() {
    setCreateError(null);
    setSuccessMsg(null);
    setCode(suggestCode(accountTypeTarget, discountPercent));
    setShowCreateForm(true);
  }

  async function handleCreate(e) {
    if (e) e.preventDefault();
    const cleanCode = (code || "").trim().toUpperCase();
    if (!cleanCode || cleanCode.length < 3) {
      setCreateError("Coupon code must be at least 3 characters.");
      return;
    }
    const pct = parseInt(discountPercent, 10);
    if (isNaN(pct) || pct < 1 || pct > 100) {
      setCreateError("Discount percentage must be between 1% and 100%.");
      return;
    }
    const uses = Math.max(1, parseInt(maxUses, 10) || 1);

    let applicableAccountTypes = ["ORGANISATION", "ACADEMIC_INSTITUTION"];
    if (accountTypeTarget === "ORGANISATION") {
      applicableAccountTypes = ["ORGANISATION"];
    } else if (accountTypeTarget === "ACADEMIC_INSTITUTION") {
      applicableAccountTypes = ["ACADEMIC_INSTITUTION"];
    } else if (accountTypeTarget === "PROFESSIONAL") {
      applicableAccountTypes = ["PROFESSIONAL"];
    } else if (accountTypeTarget === "ALL") {
      applicableAccountTypes = [
        "ORGANISATION",
        "ACADEMIC_INSTITUTION",
        "PROFESSIONAL",
      ];
    }

    setCreating(true);
    setCreateError(null);
    try {
      const res = await createAdminCoupon({
        code: cleanCode,
        discountPercent: pct,
        applicableAccountTypes,
        maxUses: uses,
        targetRegistrationNumber:
          targetRegistrationNumber.trim().toUpperCase() || undefined,
        expiresAt: expiresAt ? new Date(expiresAt).toISOString() : undefined,
      });
      const created = res?.data || res;
      setCoupons((prev) => [created, ...prev]);
      setSuccessMsg(`Onboarding coupon "${cleanCode}" created successfully!`);
      setShowCreateForm(false);
      setCode("");
      setDiscountPercent(100);
      setTargetRegistrationNumber("");
      setExpiresAt("");
      setMaxUses(1);
    } catch (err) {
      setCreateError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to create coupon.",
      );
    } finally {
      setCreating(false);
    }
  }

  async function handleToggle(coupon) {
    setTogglingId(coupon.id);
    setError(null);
    setSuccessMsg(null);
    try {
      const res = await toggleAdminCoupon(coupon.id);
      const updated = res?.data || res;
      setCoupons((prev) =>
        prev.map((c) =>
          c.id === coupon.id ? { ...c, status: updated.status } : c,
        ),
      );
      setSuccessMsg(
        `Coupon "${coupon.code}" status changed to ${updated.status}.`,
      );
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to toggle coupon.",
      );
    } finally {
      setTogglingId(null);
    }
  }

  async function handleDelete(coupon) {
    if (
      !window.confirm(
        `Are you sure you want to delete coupon "${coupon.code}"? This action cannot be undone.`,
      )
    ) {
      return;
    }
    setDeletingId(coupon.id);
    setError(null);
    setSuccessMsg(null);
    try {
      await deleteAdminCoupon(coupon.id);
      setCoupons((prev) => prev.filter((c) => c.id !== coupon.id));
      setSuccessMsg(`Coupon "${coupon.code}" deleted successfully.`);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to delete coupon.",
      );
    } finally {
      setDeletingId(null);
    }
  }

  function handleCopy(cCode) {
    navigator.clipboard?.writeText(cCode);
    setCopiedCode(cCode);
    setTimeout(() => setCopiedCode(null), 2000);
  }

  // Filtered listing
  const filteredCoupons = coupons.filter((c) => {
    if (search.trim()) {
      const term = search.trim().toUpperCase();
      const codeMatch = c.code.toUpperCase().includes(term);
      const orgMatch = c.organisation?.name?.toUpperCase().includes(term);
      const instMatch = c.institution?.name?.toUpperCase().includes(term);
      if (!codeMatch && !orgMatch && !instMatch) return false;
    }
    if (statusFilter !== "ALL" && c.status !== statusFilter) {
      return false;
    }
    if (typeFilter !== "ALL") {
      const types = c.applicableAccountTypes || [];
      if (!types.includes(typeFilter)) return false;
    }
    return true;
  });

  const availableCount = coupons.filter((c) => c.status === "AVAILABLE").length;
  const usedCount = coupons.filter(
    (c) => c.status === "USED" || c.usedCount >= c.maxUses,
  ).length;
  const disabledCount = coupons.filter((c) => c.status === "DISABLED").length;

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Navigation & Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-slate-200 pb-5">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Link
                to="/admin"
                className="text-xs font-semibold text-blue-600 hover:text-blue-800 transition-colors flex items-center gap-1"
              >
                &larr; Back to Admin Dashboard
              </Link>
            </div>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">
              Onboarding Coupons
            </h1>
            <p className="text-xs sm:text-sm text-slate-600 mt-0.5">
              Generate and manage promotional coupons used by Organisations and
              Academic Institutions during onboarding.
            </p>
          </div>

          <div className="flex items-center gap-2.5 shrink-0">
            <button
              type="button"
              onClick={loadCoupons}
              disabled={loading}
              className="px-3.5 py-2 text-xs font-semibold rounded-xl border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 transition cursor-pointer flex items-center gap-1.5 shadow-sm"
              title="Refresh coupon list"
            >
              <svg
                className={`w-3.5 h-3.5 ${loading ? "animate-spin text-blue-600" : "text-slate-500"}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              <span>Refresh</span>
            </button>

            {!showCreateForm && (
              <button
                type="button"
                onClick={handleOpenCreate}
                className="px-4 py-2 text-xs font-bold rounded-xl bg-[#16294C] hover:bg-[#0f1d36] text-white shadow-md transition flex items-center gap-1.5 cursor-pointer"
              >
                <svg
                  className="w-3.5 h-3.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2.5"
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                <span>Generate Coupon</span>
              </button>
            )}
          </div>
        </div>

        {/* Global Notifications */}
        {error && (
          <div className="p-3.5 bg-red-50 border border-red-200 rounded-xl text-red-700 text-xs sm:text-sm font-medium flex items-center justify-between">
            <span>{error}</span>
            <button
              type="button"
              onClick={() => setError(null)}
              className="text-red-500 hover:text-red-700 text-xs font-bold"
            >
              Dismiss
            </button>
          </div>
        )}

        {successMsg && (
          <div className="p-3.5 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-800 text-xs sm:text-sm font-medium flex items-center justify-between shadow-sm">
            <span>{successMsg}</span>
            <button
              type="button"
              onClick={() => setSuccessMsg(null)}
              className="text-emerald-600 hover:text-emerald-800 text-xs font-bold"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3.5 sm:gap-4">
          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
            <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider block">
              Total Coupons
            </span>
            <span className="text-2xl font-bold text-slate-900 mt-1 block">
              {coupons.length}
            </span>
            <span className="text-[11px] text-slate-400 mt-0.5 block">
              Generated by admin
            </span>
          </div>

          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
            <span className="text-[11px] font-semibold text-emerald-600 uppercase tracking-wider block">
              Available / Active
            </span>
            <span className="text-2xl font-bold text-emerald-600 mt-1 block">
              {availableCount}
            </span>
            <span className="text-[11px] text-slate-400 mt-0.5 block">
              Ready for onboarding
            </span>
          </div>

          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
            <span className="text-[11px] font-semibold text-blue-600 uppercase tracking-wider block">
              Redeemed
            </span>
            <span className="text-2xl font-bold text-blue-600 mt-1 block">
              {usedCount}
            </span>
            <span className="text-[11px] text-slate-400 mt-0.5 block">
              Used during signup
            </span>
          </div>

          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
            <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider block">
              Disabled
            </span>
            <span className="text-2xl font-bold text-slate-600 mt-1 block">
              {disabledCount}
            </span>
            <span className="text-[11px] text-slate-400 mt-0.5 block">
              Temporarily paused
            </span>
          </div>
        </div>

        {/* Inline Coupon Generator Card */}
        {showCreateForm && (
          <div className="bg-white border border-blue-200 rounded-2xl shadow-md p-5 sm:p-6 space-y-4 ring-2 ring-blue-500/10">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div>
                <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-blue-600 inline-block"></span>
                  Generate New Onboarding Coupon
                </h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  This coupon can only be used by organisations or academic
                  institutions during account creation.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowCreateForm(false)}
                className="text-xs font-semibold text-slate-400 hover:text-slate-600 cursor-pointer"
              >
                Cancel &times;
              </button>
            </div>

            {createError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-xs font-semibold">
                {createError}
              </div>
            )}

            <form onSubmit={handleCreate} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-12 gap-4">
                {/* Coupon Code Input */}
                <div className="sm:col-span-5">
                  <label className="block text-xs font-bold text-slate-700 mb-1.5 uppercase tracking-wider">
                    Coupon Code <span className="text-red-500">*</span>
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      required
                      placeholder="e.g. WELCOME100, IITBOMBAY-FREE"
                      value={code}
                      onChange={(e) =>
                        setCode(
                          e.target.value
                            .toUpperCase()
                            .replace(/[^A-Z0-9_-]/g, ""),
                        )
                      }
                      className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-300 rounded-xl text-xs font-mono font-bold tracking-wider text-slate-900 uppercase focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#16294C] focus:border-transparent"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setCode(suggestCode(accountTypeTarget, discountPercent))
                      }
                      className="px-3 py-2.5 text-xs font-semibold rounded-xl border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 shrink-0 cursor-pointer shadow-sm"
                      title="Auto-suggest a unique code"
                    >
                      Suggest
                    </button>
                  </div>
                  <p className="text-[11px] text-slate-500 mt-1">
                    Uppercase alphanumeric. May contain hyphens or underscores.
                  </p>
                </div>

                {/* Target Account Type */}
                <div className="sm:col-span-4">
                  <label className="block text-xs font-bold text-slate-700 mb-1.5 uppercase tracking-wider">
                    Applicable Onboarding Type{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={accountTypeTarget}
                    onChange={(e) => {
                      setAccountTypeTarget(e.target.value);
                      if (
                        !code ||
                        code.startsWith("WELCOME") ||
                        code.startsWith("TAPORG") ||
                        code.startsWith("TAPINST")
                      ) {
                        setCode(suggestCode(e.target.value, discountPercent));
                      }
                    }}
                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-300 rounded-xl text-xs font-semibold text-slate-900 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#16294C] focus:border-transparent"
                  >
                    <option value="BOTH">
                      Both Organisation &amp; Institution
                    </option>
                    <option value="ORGANISATION">Organisation Only</option>
                    <option value="ACADEMIC_INSTITUTION">
                      Academic Institution Only
                    </option>
                    <option value="PROFESSIONAL">
                      Professional Credential Verification Only
                    </option>
                    <option value="ALL">All Account Types</option>
                  </select>
                  <p className="text-[11px] text-slate-500 mt-1">
                    Coupons can be redeemed on onboarding subscriptions or
                    credential verification requests.
                  </p>
                </div>

                {/* Discount Percentage */}
                <div className="sm:col-span-3">
                  <label className="block text-xs font-bold text-slate-700 mb-1.5 uppercase tracking-wider">
                    Discount Percentage <span className="text-red-500">*</span>
                  </label>
                  <div className="flex items-center gap-1.5">
                    <input
                      type="number"
                      min="1"
                      max="100"
                      required
                      value={discountPercent}
                      onChange={(e) => {
                        const val = parseInt(e.target.value, 10);
                        setDiscountPercent(
                          isNaN(val) ? "" : Math.min(100, Math.max(1, val)),
                        );
                      }}
                      className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-300 rounded-xl text-xs font-bold text-slate-900 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#16294C] focus:border-transparent"
                    />
                    <span className="text-xs font-bold text-slate-500">%</span>
                  </div>

                  {/* Preset quick buttons */}
                  <div className="flex items-center gap-1 mt-1.5">
                    {[100, 50, 25].map((pct) => (
                      <button
                        key={pct}
                        type="button"
                        onClick={() => {
                          setDiscountPercent(pct);
                          if (
                            !code ||
                            code.startsWith("WELCOME") ||
                            code.startsWith("TAPORG") ||
                            code.startsWith("TAPINST")
                          ) {
                            setCode(suggestCode(accountTypeTarget, pct));
                          }
                        }}
                        className={`px-2 py-0.5 rounded text-[10px] font-semibold transition cursor-pointer ${
                          discountPercent === pct
                            ? "bg-[#16294C] text-white"
                            : "bg-slate-200 text-slate-700 hover:bg-slate-300"
                        }`}
                      >
                        {pct === 100 ? "100% Free" : `${pct}%`}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Entity Assignment and Expiry */}
              <div className="grid grid-cols-1 sm:grid-cols-12 gap-4 pt-3 border-t border-slate-100">
                <div className="sm:col-span-7">
                  <label className="block text-xs font-bold text-slate-700 mb-1.5 uppercase tracking-wider">
                    Assign to Specific Entity (PAN, GSTIN, or Registration No.)
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. 29ABCDE1234F1Z5 or AAACA1234F (Only this entity can redeem)"
                    value={targetRegistrationNumber}
                    onChange={(e) =>
                      setTargetRegistrationNumber(e.target.value.toUpperCase())
                    }
                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-300 rounded-xl text-xs font-mono font-semibold tracking-wider text-slate-900 uppercase focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#16294C] focus:border-transparent"
                  />
                  <p className="text-[11px] text-slate-500 mt-1">
                    Enforces strict ownership: Organisation A's coupon cannot be
                    redeemed by Organisation B.
                  </p>
                </div>

                <div className="sm:col-span-5">
                  <label className="block text-xs font-bold text-slate-700 mb-1.5 uppercase tracking-wider">
                    Expiry Date (Optional)
                  </label>
                  <input
                    type="date"
                    value={expiresAt}
                    onChange={(e) => setExpiresAt(e.target.value)}
                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-300 rounded-xl text-xs font-semibold text-slate-900 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#16294C] focus:border-transparent"
                  />
                  <p className="text-[11px] text-slate-500 mt-1">
                    Coupon will automatically expire after this date.
                  </p>
                </div>
              </div>

              {/* Advanced: Max Uses */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-3 border-t border-slate-100">
                <div className="flex items-center gap-3">
                  <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Max Allowed Redemptions:
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="1000"
                    value={maxUses}
                    onChange={(e) =>
                      setMaxUses(Math.max(1, parseInt(e.target.value, 10) || 1))
                    }
                    className="w-20 px-2.5 py-1.5 bg-slate-50 border border-slate-300 rounded-lg text-xs font-bold text-slate-900"
                  />
                  <span className="text-[11px] text-slate-500">
                    (Default 1 for single-use onboarding coupon)
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setShowCreateForm(false)}
                    className="px-4 py-2.5 text-xs font-semibold text-slate-600 hover:text-slate-800 cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={creating || !code.trim()}
                    className="px-5 py-2.5 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 rounded-xl transition shadow-md flex items-center gap-1.5 cursor-pointer"
                  >
                    {creating ? (
                      <>
                        <svg
                          className="w-3.5 h-3.5 animate-spin"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8v8H4z"
                          />
                        </svg>
                        <span>Generating...</span>
                      </>
                    ) : (
                      <>
                        <span>Confirm &amp; Generate Coupon</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}

        {/* Filter and Search Bar */}
        <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm flex flex-col sm:flex-row gap-3 items-center justify-between">
          <div className="relative w-full sm:w-80">
            <input
              type="text"
              placeholder="Search by coupon code or entity name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3.5 py-2 text-xs sm:text-sm rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#16294C]/20 focus:border-[#16294C]"
            />
            <svg
              className="w-4 h-4 text-slate-400 absolute left-3 top-2.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>

          <div className="flex flex-wrap items-center gap-2.5 w-full sm:w-auto">
            {/* Status Filter */}
            <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-xl text-xs font-semibold">
              {["ALL", "AVAILABLE", "USED", "DISABLED"].map((st) => (
                <button
                  key={st}
                  type="button"
                  onClick={() => setStatusFilter(st)}
                  className={`px-2.5 py-1 rounded-lg text-xs transition cursor-pointer ${
                    statusFilter === st
                      ? "bg-white text-slate-900 shadow-sm font-bold"
                      : "text-slate-500 hover:text-slate-800"
                  }`}
                >
                  {st === "ALL"
                    ? "All Status"
                    : st.charAt(0) + st.slice(1).toLowerCase()}
                </button>
              ))}
            </div>

            {/* Target Filter */}
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="px-3 py-1.5 text-xs font-semibold rounded-xl border border-slate-300 bg-white text-slate-700"
            >
              <option value="ALL">All Targets</option>
              <option value="ORGANISATION">Organisations</option>
              <option value="ACADEMIC_INSTITUTION">Institutions</option>
            </select>
          </div>
        </div>

        {/* Coupons Table */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs sm:text-sm">
              <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 font-bold uppercase text-[10px] sm:text-[11px] tracking-wider">
                <tr>
                  <th className="px-4 sm:px-6 py-3.5">Coupon Code</th>
                  <th className="px-4 sm:px-6 py-3.5">Discount</th>
                  <th className="px-4 sm:px-6 py-3.5">Applicable For</th>
                  <th className="px-4 sm:px-6 py-3.5">Status</th>
                  <th className="px-4 sm:px-6 py-3.5">Uses</th>
                  <th className="px-4 sm:px-6 py-3.5">Redemption Info</th>
                  <th className="px-4 sm:px-6 py-3.5">Created</th>
                  <th className="px-4 sm:px-6 py-3.5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  <tr>
                    <td
                      colSpan="8"
                      className="px-6 py-12 text-center text-slate-400"
                    >
                      <div className="flex flex-col items-center gap-2">
                        <svg
                          className="w-6 h-6 animate-spin text-blue-600"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8v8H4z"
                          />
                        </svg>
                        <span>Loading onboarding coupons...</span>
                      </div>
                    </td>
                  </tr>
                ) : filteredCoupons.length === 0 ? (
                  <tr>
                    <td
                      colSpan="8"
                      className="px-6 py-12 text-center text-slate-500"
                    >
                      <div className="max-w-md mx-auto space-y-2">
                        <svg
                          className="w-10 h-10 text-slate-300 mx-auto"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="1.5"
                            d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"
                          />
                        </svg>
                        <p className="font-semibold text-slate-700">
                          No onboarding coupons found
                        </p>
                        <p className="text-xs text-slate-400">
                          {search ||
                          statusFilter !== "ALL" ||
                          typeFilter !== "ALL"
                            ? "No coupons match your filter criteria. Try clearing search or filters."
                            : 'Click "Generate Coupon" above to create a new promotional code for organisation or institution onboarding.'}
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredCoupons.map((c) => {
                    const isFullyUsed =
                      c.status === "USED" || c.usedCount >= c.maxUses;
                    const isAvailable =
                      c.status === "AVAILABLE" && !isFullyUsed;
                    const isDisabled = c.status === "DISABLED";

                    return (
                      <tr
                        key={c.id}
                        onClick={() => {
                          if (c.organisation || c.institution) {
                            handleOpenEntity(c);
                          }
                        }}
                        className={`transition-colors ${
                          c.organisation || c.institution
                            ? "hover:bg-blue-50/60 cursor-pointer"
                            : "hover:bg-slate-50/70"
                        }`}
                      >
                        {/* Code */}
                        <td className="px-4 sm:px-6 py-4">
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-bold text-slate-900 tracking-wider text-xs sm:text-sm bg-slate-100 px-2.5 py-1 rounded-lg border border-slate-200">
                              {c.code}
                            </span>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCopy(c.code);
                              }}
                              className="text-slate-400 hover:text-slate-700 p-1 rounded hover:bg-slate-200 transition cursor-pointer"
                              title="Copy code to clipboard"
                            >
                              {copiedCode === c.code ? (
                                <span className="text-[10px] font-bold text-emerald-600">
                                  Copied!
                                </span>
                              ) : (
                                <svg
                                  className="w-3.5 h-3.5"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                                  />
                                </svg>
                              )}
                            </button>
                          </div>
                        </td>

                        {/* Discount */}
                        <td className="px-4 sm:px-6 py-4 font-semibold text-slate-900">
                          {c.discountPercent === 100 ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800">
                              100% (FREE)
                            </span>
                          ) : (
                            <span>{c.discountPercent}% OFF</span>
                          )}
                        </td>

                        {/* Applicable Targets */}
                        <td className="px-4 sm:px-6 py-4">
                          <div className="flex flex-wrap gap-1">
                            {c.applicableAccountTypes?.includes(
                              "ORGANISATION",
                            ) && (
                              <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-blue-50 text-blue-700 border border-blue-200">
                                Organisation
                              </span>
                            )}
                            {c.applicableAccountTypes?.includes(
                              "ACADEMIC_INSTITUTION",
                            ) && (
                              <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-purple-50 text-purple-700 border border-purple-200">
                                Institution
                              </span>
                            )}
                          </div>
                        </td>

                        {/* Status */}
                        <td className="px-4 sm:px-6 py-4">
                          {isAvailable && (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                              AVAILABLE
                            </span>
                          )}
                          {isFullyUsed && (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold bg-blue-50 text-blue-700 border border-blue-200">
                              <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
                              USED
                            </span>
                          )}
                          {isDisabled && (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold bg-slate-100 text-slate-600 border border-slate-300">
                              <span className="w-1.5 h-1.5 rounded-full bg-slate-400"></span>
                              DISABLED
                            </span>
                          )}
                        </td>

                        {/* Usage */}
                        <td className="px-4 sm:px-6 py-4 text-xs font-semibold text-slate-700">
                          {c.usedCount} / {c.maxUses}
                        </td>

                        {/* Redemption Info */}
                        <td className="px-4 sm:px-6 py-4">
                          {c.organisation ? (
                            <div
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenEntity(c);
                              }}
                              className="group/entity inline-block p-1.5 -m-1.5 rounded-xl hover:bg-blue-100/70 transition cursor-pointer"
                              title="Click to view full Organisation details"
                            >
                              <div className="flex items-center gap-1.5">
                                <span className="font-semibold text-slate-900 block text-xs group-hover/entity:text-blue-700 group-hover/entity:underline">
                                  {c.organisation.name}
                                </span>
                                <svg
                                  className="w-3.5 h-3.5 text-blue-500 opacity-60 group-hover/entity:opacity-100 transition-opacity shrink-0"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                                  />
                                </svg>
                              </div>
                              <span className="text-[11px] text-slate-500 block">
                                Organisation •{" "}
                                {c.organisation.registrationNumber}
                              </span>
                              {loadingEntityId === c.organisation.id && (
                                <span className="text-[10px] text-blue-600 font-medium flex items-center gap-1 mt-0.5">
                                  <svg
                                    className="w-2.5 h-2.5 animate-spin"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                  >
                                    <circle
                                      className="opacity-25"
                                      cx="12"
                                      cy="12"
                                      r="10"
                                      stroke="currentColor"
                                      strokeWidth="4"
                                    />
                                    <path
                                      className="opacity-75"
                                      fill="currentColor"
                                      d="M4 12a8 8 0 018-8v8H4z"
                                    />
                                  </svg>
                                  Loading details...
                                </span>
                              )}
                            </div>
                          ) : c.institution ? (
                            <div
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenEntity(c);
                              }}
                              className="group/entity inline-block p-1.5 -m-1.5 rounded-xl hover:bg-purple-100/70 transition cursor-pointer"
                              title="Click to view full Academic Institution details"
                            >
                              <div className="flex items-center gap-1.5">
                                <span className="font-semibold text-slate-900 block text-xs group-hover/entity:text-purple-700 group-hover/entity:underline">
                                  {c.institution.name}
                                </span>
                                <svg
                                  className="w-3.5 h-3.5 text-purple-500 opacity-60 group-hover/entity:opacity-100 transition-opacity shrink-0"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                                  />
                                </svg>
                              </div>
                              <span className="text-[11px] text-slate-500 block">
                                Institution • {c.institution.registrationNumber}
                              </span>
                              {loadingEntityId === c.institution.id && (
                                <span className="text-[10px] text-purple-600 font-medium flex items-center gap-1 mt-0.5">
                                  <svg
                                    className="w-2.5 h-2.5 animate-spin"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                  >
                                    <circle
                                      className="opacity-25"
                                      cx="12"
                                      cy="12"
                                      r="10"
                                      stroke="currentColor"
                                      strokeWidth="4"
                                    />
                                    <path
                                      className="opacity-75"
                                      fill="currentColor"
                                      d="M4 12a8 8 0 018-8v8H4z"
                                    />
                                  </svg>
                                  Loading details...
                                </span>
                              )}
                            </div>
                          ) : c.redeemedUser ? (
                            <div>
                              <span className="font-semibold text-slate-900 block text-xs">
                                {c.redeemedUser.email}
                              </span>
                            </div>
                          ) : (
                            <span className="text-slate-400 text-xs italic">
                              Not redeemed yet
                            </span>
                          )}
                          {c.redeemedAt && (
                            <span className="text-[10px] text-slate-400 block mt-0.5">
                              {new Date(c.redeemedAt).toLocaleDateString(
                                "en-IN",
                                {
                                  day: "numeric",
                                  month: "short",
                                  year: "numeric",
                                },
                              )}
                            </span>
                          )}
                        </td>

                        {/* Created At */}
                        <td className="px-4 sm:px-6 py-4 text-slate-500 text-xs whitespace-nowrap">
                          {new Date(c.createdAt).toLocaleDateString("en-IN", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          })}
                        </td>

                        {/* Actions */}
                        <td className="px-4 sm:px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            {!isFullyUsed && (
                              <button
                                type="button"
                                disabled={togglingId === c.id}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleToggle(c);
                                }}
                                className={`px-2.5 py-1 text-[11px] font-bold rounded-lg border transition cursor-pointer ${
                                  c.status === "AVAILABLE"
                                    ? "border-amber-200 text-amber-700 bg-amber-50 hover:bg-amber-100"
                                    : "border-emerald-200 text-emerald-700 bg-emerald-50 hover:bg-emerald-100"
                                }`}
                              >
                                {togglingId === c.id
                                  ? "Updating..."
                                  : c.status === "AVAILABLE"
                                    ? "Disable"
                                    : "Enable"}
                              </button>
                            )}

                            {c.usedCount === 0 && !isFullyUsed && (
                              <button
                                type="button"
                                disabled={deletingId === c.id}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDelete(c);
                                }}
                                className="px-2.5 py-1 text-[11px] font-bold rounded-lg border border-red-200 text-red-600 bg-red-50 hover:bg-red-100 transition cursor-pointer"
                                title="Delete unused coupon"
                              >
                                {deletingId === c.id ? "..." : "Delete"}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Institution Details Modal */}
        {selectedInstDetail && (
          <InstitutionDetailsModal
            inst={selectedInstDetail}
            onClose={() => setSelectedInstDetail(null)}
            onUpdated={(updatedInst) => {
              setSelectedInstDetail((prev) => ({ ...prev, ...updatedInst }));
            }}
          />
        )}

        {/* Organisation Details Modal */}
        {selectedOrgDetail && (
          <OrganisationDetailsModal
            org={selectedOrgDetail}
            onClose={() => setSelectedOrgDetail(null)}
            onUpdated={(updatedOrg) => {
              setSelectedOrgDetail((prev) => ({ ...prev, ...updatedOrg }));
            }}
          />
        )}
      </div>
    </AppShell>
  );
}
